import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { documentService } from '@/lib/api/services/document'
import type {
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  Document,
  CreateDocument,
  UpdateDocument,
  UpdateDocumentStatus,
  DocumentFilterParams,
  DocumentStats
} from '@/lib/api/types'

// Query keys for caching
export const documentKeys = {
  all: ['documents'] as const,
  lists: () => [...documentKeys.all, 'list'] as const,
  list: (filters?: DocumentFilterParams) => [...documentKeys.lists(), filters] as const,
  details: () => [...documentKeys.all, 'detail'] as const,
  detail: (id: string) => [...documentKeys.details(), id] as const,
  filtered: (params: PaginationParams, filters: DocumentFilterParams) =>
    [...documentKeys.all, 'filtered', params, filters] as const,
  search: (query: string) => [...documentKeys.all, 'search', query] as const,
  stats: () => [...documentKeys.all, 'stats'] as const
}

// Hook to filter documents with pagination
export function useFilterDocuments(
  params: PaginationParams = { page: 1, size: 10 },
  filters: DocumentFilterParams = {},
  enabled?: boolean
) {
  return useQuery<ApiResponse<PaginatedResponse<Document>>, Error>({
    queryKey: documentKeys.filtered(params, filters),
    queryFn: () => documentService.filterDocuments(params, filters),
    enabled: enabled
  })
}

// Hook to fetch a single document
export function useDocument(documentId?: string) {
  return useQuery<ApiResponse<Document>, Error>({
    queryKey: documentKeys.detail(documentId!),
    queryFn: () => documentService.getDocumentById(documentId!),
    enabled: !!documentId
  })
}

// Hook to create a document
export function useCreateDocument() {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<Document>, Error, CreateDocument>({
    mutationFn: (data) => documentService.createDocument(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: documentKeys.lists() })
      queryClient.invalidateQueries({ queryKey: documentKeys.all })
      queryClient.invalidateQueries({ queryKey: documentKeys.stats() })
    }
  })
}

// Hook to update a document
export function useUpdateDocument() {
  const queryClient = useQueryClient()

  return useMutation<
    ApiResponse<Document>,
    Error,
    { documentId: string; data: UpdateDocument }
  >({
    mutationFn: ({ documentId, data }) =>
      documentService.updateDocument(documentId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: documentKeys.lists() })
      queryClient.invalidateQueries({ queryKey: documentKeys.all })
      queryClient.invalidateQueries({ queryKey: documentKeys.detail(variables.documentId) })
      queryClient.invalidateQueries({ queryKey: documentKeys.stats() })
    }
  })
}

// Hook to update document status
export function useUpdateDocumentStatus() {
  const queryClient = useQueryClient()

  return useMutation<
    ApiResponse<null>,
    Error,
    { documentId: string; data: UpdateDocumentStatus }
  >({
    mutationFn: ({ documentId, data }) =>
      documentService.updateDocumentStatus(documentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: documentKeys.lists() })
      queryClient.invalidateQueries({ queryKey: documentKeys.all })
      queryClient.invalidateQueries({ queryKey: documentKeys.stats() })
    }
  })
}

// Hook to delete a document
export function useDeleteDocument() {
  const queryClient = useQueryClient()

  return useMutation<
    ApiResponse<null>,
    Error,
    { documentId: string }
  >({
    mutationFn: ({ documentId }) =>
      documentService.deleteDocument(documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: documentKeys.lists() })
      queryClient.invalidateQueries({ queryKey: documentKeys.all })
      queryClient.invalidateQueries({ queryKey: documentKeys.stats() })
    }
  })
}

// Hook to fetch draft documents count
export function useDraftDocumentsCount() {
  return useQuery<ApiResponse<number>, Error>({
    queryKey: [...documentKeys.stats(), 'draft'],
    queryFn: () => documentService.getDraftDocumentsCount()
  })
}

// Hook to fetch total documents count (published)
export function useTotalDocumentsCount() {
  return useQuery<ApiResponse<number>, Error>({
    queryKey: [...documentKeys.stats(), 'total'],
    queryFn: () => documentService.getTotalDocumentsCount()
  })
}

// Hook to search documents
export function useSearchDocuments(
  searchBy?: string,
  enabled: boolean = true
) {
  return useQuery<ApiResponse<Document[]>, Error>({
    queryKey: documentKeys.search(searchBy || ''),
    queryFn: () => documentService.searchDocuments(searchBy),
    enabled: enabled && !!searchBy && searchBy.length > 0,
    staleTime: 2 * 60 * 1000 // 2 minutes
  })
}

// Hook to get document categories
export function useDocumentCategories() {
  return useQuery<ApiResponse<Array<{ value: string; displayName: string }>>, Error>({
    queryKey: [...documentKeys.all, 'categories'],
    queryFn: () => documentService.getDocumentCategories(),
    staleTime: 30 * 60 * 1000 // 30 minutes
  })
}

// Hook to get document stats
export function useDocumentStats() {
  return useQuery<DocumentStats, Error>({
    queryKey: documentKeys.stats(),
    queryFn: async () => {
      const response = await documentService.getDocumentStats()
      return response.data
    },
    staleTime: 5 * 60 * 1000 // 5 minutes
  })
}